import React from 'react';
import { API_ROOT, authHeaders, qfetch } from '../utils/http.js';
import { t } from '../utils/lang.js';
import { khatmAPI, userAPI } from '../utils/api.js';

const KhatmContext = React.createContext(null);


function repairMyUnits(task) {
  if (!task || !task.isGroup || !Array.isArray(task.participants) || !Array.isArray(task.units)) return task;
  const me = getTelegramUserUnsafe();
  const pid = 'tg:' + me.id;
  let changed = false;
  const parts = task.participants.map(p => {
    if (p.id !== pid) return p;
    const pu = Array.isArray(p.units) ? p.units : [];
    const hasJuz0 = pu.some(u => Number(u?.juz) === 0);
    const lenMismatch = ((task.units || []).length > 0) && pu.length !== (task.units || []).length;
    if (hasJuz0 || lenMismatch || pu.length === 0) {
      changed = true;
      const merged = mergeUnitsByJuz(pu, task.units);
      return { ...p, units: merged, joined: true };
    }
    return p;
  });
  if (!changed) return task;
  return { ...task, participants: parts };
}

function normalizeGroupTask(task) {
  if (!task || !task.isGroup || !Array.isArray(task.participants)) return task;
  const me = getTelegramUserUnsafe();
  const myPid = 'tg:' + me.id;
  const myUname = (me.username || '').replace(/^@/, '').toLowerCase();

  const parts = task.participants || [];
  const out = [];
  const byToken = new Set();
  const byUsername = new Set();

  let ownerAdded = false;
  let selfAlreadyAdded = false;
  for (const p of parts) {
    if ((p.role === 'owner' || p.isOwner) && !ownerAdded) {
      ownerAdded = true;
      const uname = p.username ? String(p.username).replace(/^@/, '').toLowerCase() : '';
      if (uname) byUsername.add(uname);
      out.push(p);
      if (p.id === myPid || (uname && myUname && uname === myUname) || (String(p.telegramId || '') === String(me.id || ''))) {
        selfAlreadyAdded = true;
      }
    }
  }

  let self = parts.find(p => p && p.id === myPid)
    || parts.find(p => p && String(p.telegramId || '') === String(me.id || ''))
    || null;
  if (self && !selfAlreadyAdded && !out.some(x => x.id === myPid)) {
    const unameSelf = (self.username || '').replace(/^@/, '').toLowerCase();
    if (unameSelf) byUsername.add(unameSelf);
    out.push(self);
  }

  for (const p of parts) {
    if (!p || (p.role === 'owner' || p.isOwner)) continue;
    if (p.id === myPid) continue;
    const uname = p.username ? String(p.username).replace(/^@/, '').toLowerCase() : '';
    if (uname && myUname && uname === myUname) continue;
    if (!p.inviteToken) continue;
    if (byToken.has(p.inviteToken)) continue;
    if (uname && byUsername.has(uname)) continue;
    byToken.add(p.inviteToken);
    if (uname) byUsername.add(uname);
    out.push(p);
  }

  for (const p of parts) {
    if (!p || (p.role === 'owner' || p.isOwner)) continue;
    if (p.inviteToken) continue;
    if (p.id === myPid) continue;
    const uname = p.username ? String(p.username).replace(/^@/, '').toLowerCase() : '';
    if (uname && (byUsername.has(uname) || (myUname && uname === myUname))) continue;
    if (uname) byUsername.add(uname);
    out.push(p);
  }

  return { ...task, participants: out };
}



async function fetchJuzIndex() {
  const ts = Date.now();
  const res = await qfetch(`index/juz_index.json?ts=${ts}`, { cache: 'no-store' });
  if (!res || !res.ok) throw new Error('Failed to load juz_index.json');
  return res.json();
}

function buildUnits(juzIndex, fromJuz, toJuz) {
  const units = [];
  let totalAyahs = 0;
  for (let j = fromJuz; j <= toJuz; j++) {
    const key = String(j);
    const bucket = Array.isArray(juzIndex[key]) ? juzIndex[key] : [];
    const ayahList = [];
    let count = 0;
    for (const b of bucket) {
      const sid = b?.surah;
      const ay = Array.isArray(b?.ayahs) ? b.ayahs : [];
      if (!sid) continue;
      for (const n of ay) {
        if (!Number.isFinite(n)) continue;
        count++;
        ayahList.push({ surah: sid, ayah: n });
      }
    }
    units.push({
      key: `juz:${j}`,
      juz: j,
      totalAyahs: count,
      readAyahs: 0,
      completed: false,
      ayahList,
    });
    totalAyahs += count;
  }
  return { units, totalAyahs };
}

function deepCopyUnits(units) {
  return (units || []).map(u => ({
    key: u.key,
    juz: u.juz,
    totalAyahs: u.totalAyahs,
    readAyahs: 0,
    completed: false,
    ayahList: Array.isArray(u.ayahList) ? u.ayahList.slice() : [],
  }));
}

function mergeUnitsByJuz(sourceUnits, templateUnits) {
  const tm = Array.isArray(templateUnits) ? templateUnits : [];
  const smap = new Map((Array.isArray(sourceUnits) ? sourceUnits : []).map(u => [Number(u.juz), u]));
  return tm.map(u => {
    const s = smap.get(Number(u.juz));
    if (!s) return { ...u, readAyahs: 0, completed: false };
    const read = Math.max(0, Math.min(u.totalAyahs || 0, Number(s.readAyahs) || 0));
    const completed = !!(Number(s.completed) || (u.totalAyahs > 0 && read >= (u.totalAyahs || 0)));
    return { ...u, readAyahs: read, completed };
  });
}

function ensureValidUnits(pUnits, taskUnits) {
  const pu = Array.isArray(pUnits) ? pUnits : [];
  const tu = Array.isArray(taskUnits) ? taskUnits : [];
  const hasJuz0 = pu.some(u => Number(u?.juz) === 0);
  const lenMismatch = (tu.length > 0) && (pu.length !== tu.length);
  if (hasJuz0 || lenMismatch || pu.length === 0) return deepCopyUnits(tu);
  return pu;
}

function applyJuzProgress(units, completedJuz, progress) {
  const out = (units || []).map(u => ({ ...u }));
  const byJ = new Map(out.map(u => [Number(u.juz), u]));
  const setRead = (j, read) => {
    const u = byJ.get(Number(j));
    if (!u) return;
    const total = Number(u.totalAyahs) || (Array.isArray(u.ayahList) ? u.ayahList.length : 0) || 0;
    const val = Math.max(0, Math.min(total, Number(read) || 0));
    if (!Number.isFinite(val)) return;
    u.readAyahs = Math.max(u.readAyahs || 0, val);
    u.completed = !!(total > 0 && u.readAyahs >= total);
  };
  if (Array.isArray(completedJuz)) {
    for (const j of completedJuz) {
      const u = byJ.get(Number(j));
      if (!u) continue;
      const total = Number(u.totalAyahs) || (Array.isArray(u.ayahList) ? u.ayahList.length : 0) || 0;
      setRead(Number(j), total);
    }
  }
  if (progress && typeof progress === 'object') {
    if (Array.isArray(progress)) {
      for (const p of progress) {
        const j = Number(p?.juz ?? p?.j);
        const r = Number(p?.readAyahs ?? p?.read ?? p?.count);
        if (Number.isFinite(j) && Number.isFinite(r)) setRead(j, r);
      }
    } else {
      for (const k of Object.keys(progress)) {
        const j = Number(k);
        const r = Number(progress[k]);
        if (Number.isFinite(j) && Number.isFinite(r)) setRead(j, r);
      }
    }
  }
  return out;
}

function applyGroupProgressFromApi(task, progressPayload, meServerUserId) {
  const data = progressPayload || null;
  if (!data || !task || !task.isGroup) return task;

  const overall = Number(data?.overallProgress);
  const overallPercent = Number.isFinite(overall) ? Math.max(0, Math.min(100, overall)) : null;

  const baseUnits = Array.isArray(task.units) ? task.units : [];
  const fFrom = Math.max(1, Math.min(30, Number(task.fromJuz) || 1));
  const fTo = Math.max(fFrom, Math.min(30, Number(task.toJuz) || fFrom));
  const inRange = (j) => {
    const n = Number(j);
    return Number.isFinite(n) && n >= fFrom && n <= fTo;
  };

  const partsApi = Array.isArray(data?.participants) ? data.participants : [];
  const me = getTelegramUserUnsafe();
  const myPid = 'tg:' + me.id;
  const meName = (me.username || '').replace(/^@/, '').toLowerCase();

  const existing = Array.isArray(task.participants) ? task.participants : [];
  const localSelf = existing.find(p => p && p.id === myPid) || null;
  const localSelfIsOwner = !!(localSelf && (localSelf.isOwner || localSelf.role === 'owner'));

  const makeUnitsFromProgress = (juzProgress) => {
    const src = Array.isArray(juzProgress) ? juzProgress : [];
    const byJ = new Map(src.filter(p => inRange(p?.juz)).map(p => [Number(p.juz), p]));
    return baseUnits
      .filter(u => inRange(u?.juz))
      .map(u => {
        const p = byJ.get(Number(u.juz));
        if (!p) return { ...u, readAyahs: 0, completed: false };
        const total = Math.max(0, Number(p?.totalAyahs) || Number(u.totalAyahs) || 0);
        const read = Math.max(0, Math.min(total, Number(p?.ayahsRead) || 0));
        const completed = !!(p?.completed || (total > 0 && read >= total));
        return { ...u, totalAyahs: total, readAyahs: read, completed };
      });
  };

  const apiSelf = partsApi.find(p => meServerUserId && String(p?.userId || '') === String(meServerUserId))
    || partsApi.find(p => {
      const u1 = String(p?.username || '').replace(/^@/, '').toLowerCase();
      const u2 = String(p?.userName || '').replace(/^@/, '').toLowerCase();
      return (!!u1 && !!meName && u1 === meName) || (!!u2 && !!meName && u2 === meName);
    })
    || ((Number(task?.participantCount) === 1 && partsApi.length === 1) ? partsApi[0] : null)
    || (localSelfIsOwner ? (partsApi.find(p => p && p.isOwner) || null) : null)
    || null;

  const effectiveSelf = apiSelf
    || (meServerUserId && String(task.ownerId || '') === String(meServerUserId) ? (partsApi.find(p => p && p.isOwner) || null) : null);

  const placeholders = existing.filter(p => p && p.inviteToken && !p.joined);

  const out = [];
  const seen = new Set();

  if (effectiveSelf) {
    const units = makeUnitsFromProgress(effectiveSelf.juzProgress);
    const displayName = effectiveSelf.userName || effectiveSelf.username || null;
    const isOwner = !!effectiveSelf.isOwner || (meServerUserId && String(task.ownerId || '') === String(meServerUserId));
    out.push({ id: myPid, role: isOwner ? 'owner' : 'member', isOwner, username: me.username || null, firstName: me.firstName || null, displayName, joined: true, serverUserId: effectiveSelf.userId || null, units });
    seen.add(myPid);
  } else {
    const selfLocal = existing.find(p => p && p.id === myPid) || null;
    const units = ensureValidUnits(selfLocal?.units, task.units);
    out.push({ ...(selfLocal || { id: myPid, role: 'member', username: me.username || null, joined: true }), units });
    seen.add(myPid);
  }

  for (const p of partsApi) {
    if (!p) continue;
    const pU1 = String(p?.username || '').replace(/^@/, '').toLowerCase();
    const pU2 = String(p?.userName || '').replace(/^@/, '').toLowerCase();
    const isSelf = (meServerUserId && String(p?.userId || '') === String(meServerUserId))
      || (meName && ((pU1 && pU1 === meName) || (pU2 && pU2 === meName)))
      || (effectiveSelf && String(p?.userId || '') === String(effectiveSelf?.userId || ''))
      || (p?.isOwner && meServerUserId && String(task.ownerId || '') === String(meServerUserId));
    if (isSelf) continue;
    const units = makeUnitsFromProgress(p.juzProgress);
    const displayName = p.userName || p.username || null;
    const id = p.isOwner ? 'owner' : (`srv:${p.userId || p.username || p.userName || Math.random().toString(36).slice(2, 8)}`);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      role: p.isOwner ? 'owner' : 'member',
      isOwner: !!p.isOwner,
      username: p.username || null,
      displayName,
      joined: true,
      serverUserId: p.userId || null,
      units,
    });
  }


  const totalAyahs = Number(data?.totalAyahsInKhatm);
  const nextTask = {
    ...task,
    groupPercent: (overallPercent != null ? overallPercent : task.groupPercent),
    totalAyahs: (Number.isFinite(totalAyahs) ? totalAyahs : task.totalAyahs),
    participants: out,
  };
  return recomputeAggregates(repairMyUnits(normalizeGroupTask(nextTask)));
}

function getTelegramUserUnsafe() {
  try {
    const u = window?.Telegram?.WebApp?.initDataUnsafe?.user || null;
    if (u && (u.id || u.username)) return {
      id: String(u.id || ''),
      username: u.username ? ('@' + u.username) : null,
      firstName: u.first_name || null,
    };
  } catch { }
  let devId = localStorage.getItem('devUserId');
  if (!devId) { devId = 'dev_' + Math.random().toString(36).slice(2, 8); localStorage.setItem('devUserId', devId); }
  return { id: devId, username: null, firstName: null };
}

function recomputeAggregates(task) {
  let totalAyahs = 0;
  let readAyahs = 0;
  for (const u of task.units || []) {
    totalAyahs += u.totalAyahs || 0;
    readAyahs += u.readAyahs || 0;
  }

  const units = task.units || [];
  const allCompleted = units.length > 0 && units.every(u => u.completed);
  const now = Date.now();
  let duePassed = false;
  if (task.dueDate) {
    try {
      const d = new Date(task.dueDate);
      if (!Number.isNaN(d.getTime())) {
        duePassed = d.getTime() < now;
      }
    } catch {
      duePassed = false;
    }
  }

  let status = task.status || 'active';
  if (!status) status = 'active';

  let groupPercent = null;
  if (task.isGroup) {
    const parts = Array.isArray(task.participants) ? task.participants : [];
    const contributors = parts.filter(p => p.joined || p.isOwner || p.role === 'owner');
    const n = contributors.length > 0 ? contributors.length : 1;
    const contribs = contributors.map(p => {
      const pu = Array.isArray(p.units) ? p.units : [];
      const pTot = pu.reduce((a, b) => a + (b.totalAyahs || 0), 0) || totalAyahs || 1;
      const pRead = pu.reduce((a, b) => a + (b.readAyahs || 0), 0);
      const pPerc = pTot > 0 ? (pRead / pTot) * 100 : 0;
      return Math.max(0, Math.min(100, pPerc));
    });
    const avg = contribs.length ? (contribs.reduce((a, b) => a + b, 0) / n) : 0;
    groupPercent = Math.round(avg * 100) / 100;
    readAyahs = Math.round((groupPercent / 100) * (totalAyahs || 0));
  }

  return { ...task, totalAyahs, readAyahs, status, groupPercent };
}

function updateUnitsWithPosition(units, surahId, ayahNumber) {
  let changed = false;
  const changedJuzs = [];
  const out = (units || []).map((u) => {
    if (!Array.isArray(u.ayahList) || !u.ayahList.length) return u;
    const idx = u.ayahList.findIndex(a => a.surah === surahId && a.ayah === ayahNumber);
    if (idx === -1) return u;
    const currentRead = u.readAyahs || 0;
    const total = u.totalAyahs || u.ayahList.length || 0;
    const target = Math.max(0, Math.min(total, idx + 1));
    const nextRead = Math.max(currentRead, target);
    if (nextRead === currentRead) return u;
    const completed = nextRead >= total && total > 0;
    changed = true;
    changedJuzs.push({ juz: u.juz, readAyahs: nextRead, completed });
    return { ...u, readAyahs: nextRead, completed };
  });
  return { changed, units: out, changedJuzs };
}

function updateTaskFromPosition(task, surahId, ayahNumber) {
  if (!task || task.status === 'cancelled' || task.status === 'completed') return task;
  if (task.isGroup) {
    if (!Array.isArray(task.participants)) return task;
    const me = getTelegramUserUnsafe();
    const meName = (me.username || '').replace(/^@/, '').toLowerCase();
    const pid = 'tg:' + me.id;
    let changedAny = false;
    let changedJuzsAccum = [];
    const parts = task.participants.map(p => {
      const uname = (p.username || '').replace(/^@/, '').toLowerCase();
      const meName = (me.username || '').replace(/^@/, '').toLowerCase();
      const isMe = (p.id === pid) || (!!meName && uname && uname === meName) || (String(p.telegramId || '') === String(me.id || ''));
      if (!isMe) return p;
      const baseUnits = ensureValidUnits(p.units, task.units);
      const { changed, units, changedJuzs } = updateUnitsWithPosition(baseUnits, surahId, ayahNumber);
      if (!changed) return p;
      changedAny = true;
      if (Array.isArray(changedJuzs)) changedJuzsAccum = changedJuzsAccum.concat(changedJuzs);
      return { ...p, units, joined: true };
    });
    if (!changedAny) return task;
    try {
      if (Array.isArray(changedJuzsAccum)) {
        for (const c of changedJuzsAccum) {
          khatmAPI.updateGroupUnit(task.id, c.juz, { ayahsRead: c.readAyahs, markCompleted: !!c.completed }).catch(() => { });
        }
      }
    } catch { }
    return recomputeAggregates({ ...task, participants: parts });
  }
  if (!Array.isArray(task.units)) return task;
  const { changed, units, changedJuzs } = updateUnitsWithPosition(task.units, surahId, ayahNumber);
  if (!changed) return task;
  try {
    if (Array.isArray(changedJuzs)) {
      for (const c of changedJuzs) {
        window.dispatchEvent(new CustomEvent('khatmUnitProgressLocal', { detail: { taskId: task.id, juz: c.juz, readAyahs: c.readAyahs, completed: c.completed } }));
        try { khatmAPI.updateUnit(task.id, c.juz, { ayahsRead: c.readAyahs, markCompleted: !!c.completed }).catch(() => { }); } catch { }
      }
    }
  } catch { }
  return recomputeAggregates({ ...task, units });
}

export function KhatmProvider({ children }) {
  const [tasks, setTasks] = React.useState(() => []);
  const juzIndexRef = React.useRef(null);
  const lastStablePosRef = React.useRef({ surahId: null, ayah: null, ts: 0 });
  const serverUserIdRef = React.useRef(null);
  const [serverUserId, setServerUserId] = React.useState(() => {
    try { return localStorage.getItem('khatm:serverUserId') || null; } catch { return null; }
  });
  const deletedIdsRef = React.useRef(new Set());

  React.useEffect(() => {
    if (!serverUserId) return;
    if (!serverUserIdRef.current) serverUserIdRef.current = String(serverUserId);
  }, [serverUserId]);

  React.useEffect(() => {
    (async () => {
      try {
        const me = await userAPI.get();
        const id = me?.id || me?.userId || me?.data?.id || me?.data?.userId || null;
        if (id) {
          const sid = String(id);
          serverUserIdRef.current = sid;
          setServerUserId(sid);
          try { localStorage.setItem('khatm:serverUserId', sid); } catch { }
        }
      } catch { }
    })();
  }, []);

  React.useEffect(() => {
    if (!serverUserId) return;
    setTasks(prev => prev.map(t => {
      if (!t || !t.isGroup || !Array.isArray(t.participants)) return t;
      if (String(t.ownerId || '') !== String(serverUserId)) return t;
      const me = getTelegramUserUnsafe();
      const myPid = 'tg:' + me.id;
      let changed = false;
      const parts = t.participants.map(p => {
        if (p.id !== myPid) return p;
        if (p.role === 'owner' && p.isOwner) return p;
        changed = true;
        return { ...p, role: 'owner', isOwner: true, joined: true };
      });
      return changed ? recomputeAggregates({ ...t, participants: parts }) : t;
    }));
  }, [serverUserId]);

  const ensureJuzIndex = React.useCallback(async () => {
    if (juzIndexRef.current) return juzIndexRef.current;
    try {
      const j = await fetchJuzIndex();
      juzIndexRef.current = j || {};
    } catch {
      juzIndexRef.current = {};
    }
    return juzIndexRef.current;
  }, []);

  const persist = React.useCallback((updater) => {
    setTasks(prev => updater(prev));
  }, []);

  const createTask = React.useCallback(async ({ title, fromJuz, toJuz, targetDays, isGroup = false, inviteCount = 0 }) => {
    const fj = Number(fromJuz) || 1;
    const tj = Number(toJuz) || fj;
    const from = Math.max(1, Math.min(30, fj));
    const to = Math.max(from, Math.min(30, tj));

    const idx = await ensureJuzIndex();
    const { units, totalAyahs } = buildUnits(idx, from, to);
    const now = new Date();
    const startDate = now.toISOString();
    let targetDate = null;
    if (Number.isFinite(targetDays) && targetDays > 0) {
      const d = new Date(now.getTime());
      d.setDate(d.getDate() + targetDays);
      targetDate = d.toISOString();
    }
    let id = `khatm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const baseTitle = title && String(title).trim() ? String(title).trim() : t('khatm_auto_title', { from, to });
    const me = getTelegramUserUnsafe();
    const ownerParticipant = {
      id: 'tg:' + me.id,
      role: 'owner',
      isOwner: true,
      username: me.username || null,
      firstName: me.firstName || null,
      displayName: me.firstName || null,
      joined: true,
      units: deepCopyUnits(units),
    };
    const count = isGroup ? Math.max(0, Math.min(10, Number(inviteCount) || 0)) : 0;
    const otherParticipants = isGroup ? Array.from({ length: count }).map(() => ({
      id: `inv_${Math.random().toString(36).slice(2, 10)}`,
      role: 'member',
      username: null,
      joined: false,
      inviteToken: Math.random().toString(36).slice(2, 10),
      units: deepCopyUnits(units),
    })) : [];

    const task = recomputeAggregates({
      id,
      title: baseTitle,
      fromJuz: from,
      toJuz: to,
      createdAt: startDate,
      startDate,
      targetDate,
      dueDate: isGroup ? null : targetDate,
      targetDays: Number.isFinite(targetDays) && targetDays > 0 ? targetDays : null,
      status: 'active',
      isGroup: !!isGroup,
      participants: isGroup ? [ownerParticipant, ...otherParticipants] : undefined,
      units,
      totalAyahs,
      readAyahs: 0,
    });
    let out = task;
    if (!isGroup) {
      try {
        const payload = { fromJuz: from, toJuz: to, targetDays: task.targetDays, dueDate: task.dueDate };
        const created = await khatmAPI.createTask(payload);
        const data = (created && created.data) || created || {};
        const serverId = data.id || data.taskId || data.task_id || null;
        if (serverId && serverId !== id) {
          id = serverId;
          out = { ...task, id };
        }
      } catch { }
    }
    persist(prev => [...prev, out]);
    return out;
  }, [ensureJuzIndex, persist]);

  const toggleJuzCompleted = React.useCallback((taskId, juz) => {
    persist(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const units = (t.units || []).map(u => {
        if (u.juz !== juz) return u;
        const completed = !u.completed;
        const readAyahs = completed ? u.totalAyahs : 0;
        try { if (!t.isGroup) khatmAPI.updateUnit(t.id, juz, { readAyahs, completed }).catch(() => { }); } catch { }
        return { ...u, completed, readAyahs, completedAt: completed ? new Date().toISOString() : null };
      });
      return recomputeAggregates({ ...t, units });
    }));
  }, [persist]);

  const toggleGroupJuzCompleted = React.useCallback((taskId, juz) => {
    persist(prev => prev.map(t => {
      if (t.id !== taskId || !t.isGroup || !Array.isArray(t.participants)) return t;
      const me = getTelegramUserUnsafe();
      const pid = 'tg:' + me.id;
      let changedAny = false;
      const parts = t.participants.map(p => {
        if (p.id !== pid) return p;
        const baseUnits = ensureValidUnits(p.units, t.units);
        const units = baseUnits.map(u => {
          if (u.juz !== Number(juz)) return u;
          const completed = !u.completed;
          const readAyahs = completed ? (u.totalAyahs || 0) : 0;
          changedAny = true;
          try { khatmAPI.updateGroupUnit(taskId, Number(juz), { readAyahs, completed }).catch(() => { }); } catch { }
          return { ...u, completed, readAyahs, completedAt: completed ? new Date().toISOString() : null };
        });
        return { ...p, units };
      });
      if (!changedAny) return t;
      return recomputeAggregates({ ...t, participants: parts });
    }));
  }, [persist]);

  const joinGroupByInvite = React.useCallback(async (taskId, token, fromJuzOpt, toJuzOpt) => {
    if (!token) return;
    const fromJuzNum = Number(fromJuzOpt) || null;
    const toJuzNum = Number(toJuzOpt) || null;
    const me = getTelegramUserUnsafe();
    const meName = (me.username || '').replace(/^@/, '').toLowerCase();
    const localHadGroupTask = Array.isArray(tasks) && tasks.some(t => t && t.isGroup && t.id === taskId);

    const isAlreadyMemberError = (msg) => {
      const m = String(msg || '').toLowerCase();
      return m.includes('already') || m.includes('exists') || m.includes('уже') || m.includes('409');
    };

    const showJoinAlert = (text) => {
      try {
        if (window?.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert(String(text || ''));
          return;
        }
      } catch { }
      try { alert(String(text || '')); } catch { }
    };

    try {
      const raw = localStorage.getItem('seen_khatm_groups');
      if (raw) {
        const seenIds = JSON.parse(raw);
        if (Array.isArray(seenIds)) {
          const filtered = seenIds.filter(id => id !== taskId);
          localStorage.setItem('seen_khatm_groups', JSON.stringify(filtered));
        }
      }
    } catch { }

    let preRemote = null;
    try {
      const resp0 = await khatmAPI.listGroups();
      const groups0 = Array.isArray(resp0) ? resp0 : [];
      preRemote = groups0.find(g => g && g.id === taskId) || null;
      if (preRemote && preRemote.status === 'cancelled') {
        persist(prev => prev.filter(t => t.id !== taskId));
        showJoinAlert('Этот хатм удалён');
        return;
      }
    } catch { }

    const wasMember = !!preRemote;

    let joinOk = false;
    let joinErrMsg = null;
    if (!wasMember) {
      try {
        await khatmAPI.joinGroup(taskId, token);
        joinOk = true;
      } catch (e) {
        joinErrMsg = String((e && e.message) || '');
      }
    }

    const alreadyMember = wasMember || localHadGroupTask || isAlreadyMemberError(joinErrMsg);

    let remoteFrom = null; let remoteTo = null; let remoteTitle = null; let remoteTargetDate = null; let remoteStatus = null; let remoteOwnerId = null;
    let remoteParticipantCount = null;
    try {
      const resp = await khatmAPI.listGroups();
      const groups = Array.isArray(resp) ? resp : [];
      const remote = groups.find(g => g && g.id === taskId) || null;
      if (remote && remote.status === 'cancelled') {
        persist(prev => prev.filter(t => t.id !== taskId));
        showJoinAlert('Этот хатм удалён');
        return;
      }
      if (remote) {
        remoteFrom = Number(remote.fromJuz) || null;
        remoteTo = Number(remote.toJuz) || null;
        remoteTitle = remote.name || null;
        remoteTargetDate = remote.targetDate || null;
        remoteStatus = remote.status || null;
        remoteOwnerId = remote.ownerId || null;
        remoteParticipantCount = Number(remote.participantCount);
      }
    } catch { }

    try {
      if (joinOk || alreadyMember) {
        // Гарантируем наличие group-task (создаём, если его ещё нет), чтобы затем можно было сразу применить progress.
        try {
          const fFrom = fromJuzNum || remoteFrom;
          const fTo = toJuzNum || remoteTo;
          if (fFrom && fTo) {
            const idx = await ensureJuzIndex();
            const from = Math.max(1, Math.min(30, fFrom));
            const to = Math.max(from, Math.min(30, fTo));
            const { units } = buildUnits(idx, from, to);
            const startDate = new Date().toISOString();

            persist(prev => {
              const existing = prev.find(t => t && t.isGroup && t.id === taskId) || null;
              if (existing) {
                return prev.map(t => {
                  if (t.id !== taskId || !t.isGroup) return t;
                  return {
                    ...t,
                    title: remoteTitle || t.title,
                    status: remoteStatus || t.status,
                    targetDate: remoteTargetDate || t.targetDate,
                    ownerId: remoteOwnerId || t.ownerId,
                    participantCount: Number.isFinite(remoteParticipantCount) ? remoteParticipantCount : t.participantCount,
                  };
                });
              }

              const participant = {
                id: 'tg:' + me.id,
                role: 'member',
                username: me.username || null,
                firstName: me.firstName || null,
                joined: true,
                inviteToken: null,
                units: deepCopyUnits(units),
              };

              const pre = {
                id: taskId,
                title: remoteTitle || t('khatm_auto_title', { from, to }),
                fromJuz: from,
                toJuz: to,
                createdAt: startDate,
                startDate,
                targetDate: remoteTargetDate || null,
                dueDate: null,
                targetDays: null,
                status: remoteStatus || 'active',
                isGroup: true,
                participants: [participant],
                units,
                ownerId: remoteOwnerId || null,
                participantCount: Number.isFinite(remoteParticipantCount) ? remoteParticipantCount : null,
                totalAyahs: units.reduce((a, b) => a + (b.totalAyahs || 0), 0),
                readAyahs: 0,
              };
              const task = recomputeAggregates(repairMyUnits(normalizeGroupTask(pre)));
              return [...prev, task];
            });
          }
        } catch { }

        try {
          const pr = await khatmAPI.getGroupProgress(taskId);
          persist(prev => prev.map(t => (t.isGroup && t.id === taskId) ? applyGroupProgressFromApi(t, pr, serverUserIdRef.current || null) : t));
        } catch { }
      }

      if (alreadyMember) {
        showJoinAlert('Вы уже состоите в этом хатме');
      } else if (joinOk) {
        showJoinAlert('Вы присоединились к хатму');
      } else {
        showJoinAlert('Не удалось присоединиться к хатму');
      }
    } catch { }

  }, [persist, ensureJuzIndex, tasks]);

  const cancelTask = React.useCallback(async (taskId) => {
    let target = null;
    setTasks(prev => {
      target = prev.find(t => t.id === taskId) || null;
      try { deletedIdsRef.current.add(taskId); } catch { }
      return prev.filter(t => t.id !== taskId);
    });
    if (!target) return;
    try {
      if (target.isGroup) {
        const me = getTelegramUserUnsafe();
        const myPid = 'tg:' + me.id;
        const amOwner = Array.isArray(target.participants) && target.participants.some(p => p && p.id === myPid && (p.role === 'owner' || p.isOwner));
        if (amOwner || String(target.ownerId || '') === String(serverUserIdRef.current || '')) {
          try { await khatmAPI.deleteGroup(taskId); }
          catch (e) { try { const m = String((e && e.message) || ''); if (m.includes('404')) { } else if (m.includes('403') || m.includes('405')) { await khatmAPI.updateGroupStatus(taskId, 'cancelled'); } else { await khatmAPI.updateGroupStatus(taskId, 'cancelled'); } } catch { } }
        } else {
          try { await khatmAPI.leaveGroup(taskId); } catch { }
        }
      } else {
        try { await khatmAPI.deleteTask(taskId); } catch { }
      }
    } catch { }
  }, []);

  const completeTask = React.useCallback((taskId) => {
    persist(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const base = recomputeAggregates(t);
      return { ...base, status: 'completed', completedAt: new Date().toISOString() };
    }));
  }, [persist]);

  const leaveGroup = React.useCallback(async (taskId) => {
    try {
      await khatmAPI.leaveGroup(taskId);
      persist(prev => prev.filter(t => t.id !== taskId));
    } catch (e) {
      const msg = e && e.message ? String(e.message) : '';
      if (msg.includes('owner_cannot_leave')) {
        try { await khatmAPI.updateGroupStatus(taskId, 'cancelled'); } catch { }
        persist(prev => prev.filter(t => t.id !== taskId));
      }
    }
  }, [persist]);



  const value = React.useMemo(() => ({
    tasks,
    createTask,
    toggleJuzCompleted,
    toggleGroupJuzCompleted,
    cancelTask,
    completeTask,
    joinGroupByInvite,
    leaveGroup,
    serverUserId,
    adoptGroupServerId: (localId, server) => {
      const sid = server?.id || null;
      if (!sid) return;
      persist(prev => prev.map(t => {
        if (!t.isGroup || t.id !== localId) return t;
        return {
          ...t,
          id: sid,
          ownerId: server?.ownerId || null,
          title: server?.name,
          fromJuz: server?.fromJuz,
          toJuz: server?.toJuz,
          status: server?.status,
          targetDate: server?.targetDate,
          participantCount: server?.participantCount,
        };
      }));
    },
  }), [tasks, createTask, toggleJuzCompleted, toggleGroupJuzCompleted, cancelTask, completeTask, joinGroupByInvite, leaveGroup]);

  React.useEffect(() => {
    const onStable = (e) => {
      const d = e && e.detail;
      if (d) {
        const sid = Number(d?.surahId);
        const ay = Number(d?.ayah);
        if (Number.isFinite(sid) && Number.isFinite(ay)) {
          lastStablePosRef.current = { surahId: sid, ayah: ay, ts: Date.now() };
          return;
        }
      }
      try {
        const raw = localStorage.getItem('lastReadingPosition');
        if (!raw) return;
        const pos = JSON.parse(raw);
        const sid = Number(pos?.surahId);
        const ay = Number(pos?.ayah);
        if (!Number.isFinite(sid) || !Number.isFinite(ay)) return;
        lastStablePosRef.current = { surahId: sid, ayah: ay, ts: Date.now() };
      } catch { }
    };
    try { window.addEventListener('readingPositionChanged', onStable); } catch { }
    return () => { try { window.removeEventListener('readingPositionChanged', onStable); } catch { } };
  }, []);

  React.useEffect(() => {
    const handler = (e) => {
      const detail = e && e.detail;
      const surahId = detail && detail.surahId;
      const ayah = detail && detail.ayah;
      if (!Number.isFinite(surahId) || !Number.isFinite(ayah)) return;
      let activeTaskId = null;
      try { activeTaskId = sessionStorage.getItem('khatmActiveTask') || null; } catch { activeTaskId = null; }
      if (!activeTaskId) return;
      const ls = lastStablePosRef.current || {};
      const now = Date.now();
      if (!(ls && Number.isFinite(ls.surahId) && Number.isFinite(ls.ayah))) return;
      if (!(ls.surahId === surahId && ls.ayah === ayah)) return;
      if (now - (ls.ts || 0) > 4000) return;
      persist(prev => prev.map(task => (task.id === activeTaskId ? updateTaskFromPosition(task, surahId, ayah) : task)));
    };
    try {
      window.addEventListener('readingProgressTick', handler);
    } catch { }
    return () => {
      try {
        window.removeEventListener('readingProgressTick', handler);
      } catch { }
    };
  }, [persist]);

  const socketBaseUrl = React.useMemo(() => {
    try {
      const api = String(API_ROOT || '');
      const u = new URL(api, window.location.href);
      return u.origin;
    } catch {
      return null;
    }
  }, []);

  const syncGroup = React.useCallback(async (taskId) => {
    try {
      const resp = await khatmAPI.listGroups();
      const groups = Array.isArray(resp) ? resp : [];
      const remote = groups.find(g => g && g.id === taskId) || null;
      if (!remote) return;

      if (remote.status === 'cancelled') {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        return;
      }

      setTasks(prev => prev.map(t => {
        if (t.id !== taskId || !t.isGroup || !Array.isArray(t.participants)) return t;
        return {
          ...t,
          ownerId: remote.ownerId,
          status: remote.status,
          targetDate: remote.targetDate,
          title: remote.name,
        };
      }));

      try {
        const pr = await khatmAPI.getGroupProgress(taskId);
        setTasks(prev => prev.map(t => (t.isGroup && t.id === taskId) ? applyGroupProgressFromApi(t, pr, serverUserIdRef.current || null) : t));
      } catch { }
    } catch { }
  }, []);

  const syncAllGroups = React.useCallback(async () => {
    try {
      const list = await khatmAPI.listGroups();
      const serverGroups = Array.isArray(list) ? list : [];

      const meServerUserId = serverUserIdRef.current || null;

      const me = getTelegramUserUnsafe();
      const meName = (me.username || '').replace(/^@/, '').toLowerCase();
      const idx = await ensureJuzIndex();

      persist(prev => {
        const serverIds = new Set(serverGroups.map(g => (g && g.id)));
        const cleaned = prev.filter(t => !t.isGroup || (serverIds.has(t.id) && !deletedIdsRef.current.has(t.id)));

        if (serverGroups.length === 0) return cleaned;

        const existing = new Set(cleaned.filter(t => t && t.isGroup).map(t => t.id));
        const toAdd = [];
        for (const g of serverGroups) {
          const gid = g?.id;
          if (!gid || existing.has(gid) || deletedIdsRef.current.has(gid)) continue;
          const fFrom = Math.max(1, Math.min(30, Number(g?.fromJuz) || 1));
          const fTo = Math.max(fFrom, Math.min(30, Number(g?.toJuz) || fFrom));
          const { units } = buildUnits(idx, fFrom, fTo);

          const isSelfOwner = (String(g?.ownerId || '') === String(serverUserIdRef.current || ''));
          const selfUnits = deepCopyUnits(units);

          const participantMe = {
            id: 'tg:' + me.id,
            role: isSelfOwner ? 'owner' : 'member',
            username: me.username || null,
            firstName: me.firstName || null,
            joined: true,
            units: selfUnits,
          };

          const outOthers = [];

          const task = recomputeAggregates({
            id: gid,
            title: g.name,
            fromJuz: fFrom,
            toJuz: fTo,
            createdAt: g.createdAt || new Date().toISOString(),
            startDate: g.createdAt || new Date().toISOString(),
            targetDate: g.targetDate || null,
            dueDate: null,
            targetDays: null,
            status: g.status || 'active',
            isGroup: true,
            participants: [participantMe, ...outOthers],
            ownerId: g.ownerId || null,
            participantCount: g.participantCount,
            units,
            totalAyahs: units.reduce((a, b) => a + (b.totalAyahs || 0), 0),
            readAyahs: 0,
          });
          toAdd.push(task);
        }
        if (toAdd.length === 0) return prev;
        return [...cleaned, ...toAdd];
      });

      try {
        const ids = serverGroups.map(g => g?.id).filter(Boolean);
        await Promise.all(ids.map(async (gid) => {
          try {
            const pr = await khatmAPI.getGroupProgress(gid);
            persist(prev => prev.map(t => (t.isGroup && t.id === gid) ? applyGroupProgressFromApi(t, pr, meServerUserId) : t));
          } catch { }
        }));
      } catch { }
    } catch { }
  }, [ensureJuzIndex, persist]);

  React.useEffect(() => {
    syncAllGroups();
  }, [syncAllGroups]);

  React.useEffect(() => {
    if (!serverUserId) return;
    try { syncAllGroups(); } catch { }
  }, [serverUserId, syncAllGroups]);

  React.useEffect(() => {
    let alive = true;
    let timerId = null;
    let inFlight = false;
    let failCount = 0;

    const schedule = (ms) => {
      if (!alive) return;
      if (timerId) clearTimeout(timerId);
      timerId = setTimeout(tick, ms);
    };

    const nextDelayMs = () => {
      const base = (document.visibilityState === 'hidden') ? 5 * 60 * 1000 : 60 * 1000;
      const jitter = Math.floor(Math.random() * 5000);
      const backoff = Math.min(10 * 60 * 1000, failCount ? (Math.pow(2, Math.min(6, failCount)) * 1000) : 0);
      return base + jitter + backoff;
    };

    const tick = async () => {
      if (!alive) return;
      if (inFlight) { schedule(nextDelayMs()); return; }
      inFlight = true;
      try {
        await syncAllGroups();
        failCount = 0;
      } catch {
        failCount = Math.min(10, (failCount || 0) + 1);
      } finally {
        inFlight = false;
        schedule(nextDelayMs());
      }
    };

    const onVis = () => {
      schedule(1000);
    };

    try { document.addEventListener('visibilitychange', onVis); } catch { }
    schedule(nextDelayMs());

    return () => {
      alive = false;
      if (timerId) clearTimeout(timerId);
      try { document.removeEventListener('visibilitychange', onVis); } catch { }
    };
  }, [syncAllGroups]);

  React.useEffect(() => {
    (async () => {
      try {
        const idx = await ensureJuzIndex();
        const list = await khatmAPI.listTasks();
        const items = Array.isArray(list?.data) ? list.data : (Array.isArray(list) ? list : []);
        if (!items.length) return;
        persist(prev => {
          const existing = new Set(prev.filter(t => !t.isGroup).map(t => t.id));
          const toAdd = [];
          for (const r of items) {
            const rid = r?.id || r?.taskId || r?.task_id;
            if (!rid || existing.has(rid)) continue;
            const rawFrom = Number(r?.fromJuz ?? r?.from ?? r?.from_juz);
            const rawTo = Number(r?.toJuz ?? r?.to ?? r?.to_juz);
            const from = Math.max(1, Math.min(30, Number.isFinite(rawFrom) ? rawFrom : 1));
            const to = Math.max(from, Math.min(30, Number.isFinite(rawTo) ? rawTo : from));
            const { units, totalAyahs } = buildUnits(idx, from, to);
            const unitsWithProgress = applyJuzProgress(units, r?.completedJuz || r?.myCompletedJuz, r?.juzProgress || r?.progress || r?.juz_read_ayahs);
            const pre = {
              id: rid,
              title: r?.title || t('khatm_auto_title', { from, to }),
              fromJuz: from,
              toJuz: to,
              createdAt: r?.createdAt || new Date().toISOString(),
              startDate: r?.startDate || r?.createdAt || null,
              dueDate: r?.dueDate || null,
              targetDays: r?.targetDays || null,
              status: r?.status || 'active',
              isGroup: false,
              units: unitsWithProgress,
              totalAyahs,
              readAyahs: 0,
            };
            toAdd.push(recomputeAggregates(pre));
          }
          if (toAdd.length === 0) return prev;
          return [...prev, ...toAdd];
        });
      } catch { }
    })();
  }, [ensureJuzIndex, persist]);

  React.useEffect(() => {
    const handledKey = 'khatm:lastHandledStartParam';
    const parseAndJoin = () => {
      try {
        const tg = window?.Telegram?.WebApp;
        const raw = (() => {
          try {
            const sp = tg?.initDataUnsafe?.start_param || '';
            if (sp) return String(sp);
          } catch { }
          try {
            const u = new URL(window.location.href);
            return (
              u.searchParams.get('startapp')
              || u.searchParams.get('start_param')
              || u.searchParams.get('start')
              || ''
            );
          } catch { }
          return '';
        })();
        if (!raw) return false;
        const already = localStorage.getItem(handledKey);
        if (already && already === raw) return true;
        const restoreUuid = (hex32) => {
          const s = String(hex32 || '').trim();
          if (!/^[0-9a-f]{32}$/i.test(s)) return null;
          return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
        };

        const parts = String(raw).split('-');
        if (!parts.length) return false;

        const head = String(parts[0] || '').toLowerCase();
        if (head === 'ig') {
          const gid = restoreUuid(parts[1]);
          const token = parts[2] || null;
          if (gid && token) {
            joinGroupByInvite(gid, token, null, null);
            try { localStorage.setItem(handledKey, raw); } catch { }
            return true;
          }
          return false;
        }

        if (head !== 'invite') return false;
        const taskId = parts[1];
        const token = parts[2];
        const from = parts[3] ? parseInt(parts[3], 10) : null;
        const to = parts[4] ? parseInt(parts[4], 10) : null;
        if (taskId && token) {
          joinGroupByInvite(taskId, token, from, to);
          try { localStorage.setItem(handledKey, raw); } catch { }
          return true;
        }
      } catch { }
      return false;
    };

    let done = parseAndJoin();

    let attempts = 0;
    let intervalId = null;
    if (!done) {
      intervalId = setInterval(() => {
        attempts++;
        if (parseAndJoin() || attempts >= 6) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 500);
    }

    const onFocus = () => { parseAndJoin(); };
    try { window.addEventListener('focus', onFocus); } catch { }

    return () => {
      if (intervalId) clearInterval(intervalId);
      try { window.removeEventListener('focus', onFocus); } catch { }
    };
  }, [joinGroupByInvite]);

  return (
    <KhatmContext.Provider value={value}>
      {children}
    </KhatmContext.Provider>
  );
}

export function useKhatmTasks() {
  const ctx = React.useContext(KhatmContext);
  if (!ctx) {
    throw new Error('useKhatmTasks must be used within KhatmProvider');
  }
  return ctx;
}
